import React, { useState, useEffect, useCallback } from 'react';
import { X, Megaphone, Bell, Rocket, Tag } from 'lucide-react';
import { getActiveNotification } from '../api/Notifications.api'; 
import '../style/NotificationBanner.css';

export interface Anuncio {
    id?: string;
    isActive: boolean;
    type: string;
    title: string;
    description: string;
    buttonIsActive: boolean;
    buttonText: string;
    buttonLink: string;
}

const NotificationBanner: React.FC = () => {
  const [data, setData] = useState<Anuncio | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  // Mapeo de iconos según el tipo que venga de la BD
  const IconMap = {
    promo: Tag,
    cohete: Rocket,
    anuncio: Megaphone
  };

  const fetchAnnouncement = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getActiveNotification();
      
      // Si viene un array (ej: [{...}]), tomamos el primero. Si es objeto ({...}), lo usamos directo.
      const announcement = Array.isArray(response) ? response[0] : response;

      // Validamos que la respuesta tenga datos y esté activa
      if (announcement && announcement.isActive) {
        setData(announcement);
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } catch (error) {
      console.error("Error al cargar el anuncio:", error);
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  const onAction = () => {
    if (data?.buttonLink) {
      let url = data.buttonLink;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }
      window.open(url, '_blank');
    }
  }

  // Si está cargando, o el usuario lo cerró, o no hay data activa, no renderiza nada
  if (loading || !isVisible || !data) return null;

  // Seleccionamos el icono
  const Icon = (IconMap as any)[data.type] || Bell;

  // Generamos la clase del tema basada en data.type
  const themeClass = data.type ? `nb-theme-${data.type}` : 'nb-theme-promo';

  // Franja de una sola línea (~40 px): ícono chico, título + descripción
  // recortados y el botón como link. Antes ocupaba ~190 px en el celular.
  return (
    <div className={`nb-main-wrapper nb-strip ${themeClass}`} role="status">
      <div className="nb-inner-box">
        <div className="nb-light-sweep" />
        <div className="flex items-center gap-2 relative z-10 min-w-0">
          <div className="nb-icon-box nb-icon-sm shrink-0">
            <Icon className="text-white" size={14} />
          </div>
          <p className="flex-1 min-w-0 truncate text-xs md:text-sm text-gray-700" title={`${data.title}${data.description ? ` · ${data.description}` : ''}`}>
            <span className="font-bold text-gray-900 uppercase tracking-tight">{data.title}</span>
            {data.description && <span className="hidden sm:inline"> · {data.description}</span>}
          </p>
          {data.buttonIsActive && (
            <button type="button" className="shrink-0 text-xs md:text-sm font-semibold text-blue-700 hover:underline whitespace-nowrap" onClick={onAction}>
              {data.buttonText} →
            </button>
          )}
          <button
            type="button"
            aria-label="Cerrar novedades"
            onClick={() => setIsVisible(false)}
            className="shrink-0 p-1 -mr-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationBanner;